import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { listenToAllChannelsForUser, listenToMyChats } from '../firebase/firebaseFunctions';
import { listenForForegroundPush, requestPushPermission } from '../firebase/push';
import useFirebaseAuthReady from '../firebase/useFirebaseAuthReady';
import { chatDock, dockConversationOf, isOnChatScreen } from '../modules/providers/chat/chatDockStore';
import { previewOf } from '../modules/providers/chat/chatFormat';
import { isConversationUnread } from '../modules/providers/chat/chatUnread';

// The provider being worked on: the one in the URL (/providers/:id/…), else the
// one last loaded into Redux (kept in a module variable by the hook below).
let loadedProviderId = null;
const workingProviderId = () => {
  const fromUrl = (window.location.pathname.match(/\/providers\/(\d+)/) || [])[1];
  return fromUrl || (loadedProviderId ? String(loadedProviderId) : null);
};

// A message reaches a focused tab twice — the Firestore listener and the
// foreground push — so a conversation toasted in the last few seconds isn't again.
const TOAST_DEDUP_MS = 8000;
const recentToasts = new Map();
const toastedRecently = conversationId => {
  if (!conversationId) return false;
  const now = Date.now();
  const last = recentToasts.get(String(conversationId));
  recentToasts.set(String(conversationId), now);
  return last !== undefined && now - last < TOAST_DEDUP_MS;
};

const useUnreadMessages = () => {
  const { t } = useTranslation();
  // .kiro/specs/1600-cognito-firestore-auth — a listener started before the
  // Firebase Auth sign-in resolves gets permission-denied and never
  // self-heals; see firebase/useFirebaseAuthReady.js.
  const firebaseAuthReady = useFirebaseAuthReady();
  const authData = useSelector(state => state?.auth?.data);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const chatCountRef = useRef(0);
  const channelCountRef = useRef(0);
  const previousTotalRef = useRef(0);
  const isInitializedRef = useRef(false);
  const previousChatCountRef = useRef(0);
  const previousChannelCountRef = useRef(0);
  const lastNotificationRef = useRef(null);
  // Per provider and type: the latest unread already there when it was first looked at.
  const baselineRef = useRef({});

  const userId = authData?.userId;
  loadedProviderId = useSelector(state => state?.provider?.generalInfo?.data?.id) || loadedProviderId;

  const showToast = useCallback(
    ({ title, message, type = 'chat', count = 1, senderImage = null, providerName = null, href = '/chat' }) => {
      const icon = type === 'channel' ? '📢' : '💬';
      const typeLabel = type === 'channel' ? t('CHAT.channel') : t('CHAT.chat');
      const countText = count > 1 ? ` (${count} ${t('CHAT.newMessages').toLowerCase()})` : '';

      const avatarHtml = senderImage
        ? `<img src="${senderImage}" style="width: 48px; height: 48px; border-radius: 50%; object-fit: cover; border: 2px solid #E6616A;" alt="" />`
        : `<div style="font-size: 32px;">${icon}</div>`;

      const toast = document.createElement('div');
      toast.innerHTML = `
      <div style="display: flex; align-items: start; gap: 12px;">
        ${avatarHtml}
        <div style="flex: 1;">
          <div style="font-weight: 600; margin-bottom: 4px; font-size: 15px;">${title}</div>
          <div style="font-size: 13px; color: #6b7280; margin-bottom: 6px; line-height: 1.4; max-height: 60px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${message}${countText}</div>
          <div style="font-size: 12px; color: #9ca3af; display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
            <span style="background: #E6616A; color: white; padding: 2px 8px; border-radius: 4px; font-weight: 500;">${typeLabel}</span>
            ${providerName ? `<span>• ${providerName}</span>` : ''}
          </div>
        </div>
      </div>
    `;
      toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 20px;
      background: white;
      color: #1f2937;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
      z-index: 9999;
      cursor: pointer;
      animation: slideIn 0.3s ease-out;
      min-width: 300px;
      border-left: 4px solid #E6616A;
    `;

      const style = document.createElement('style');
      style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(400px);
          opacity: 0;
        }
      }
    `;
      if (!document.getElementById('toast-styles')) {
        style.id = 'toast-styles';
        document.head.appendChild(style);
      }

      toast.onclick = () => {
        window.location.href = href;
      };

      document.body.appendChild(toast);

      setTimeout(() => {
        toast.style.animation = 'slideOut 0.3s ease-out';
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 300);
      }, 5000);
    },
    [t]
  );

  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      const playBeep = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = audioContext.currentTime;
      playBeep(800, now, 0.15);
      playBeep(1000, now + 0.15, 0.15);
    } catch (err) {
      // Silent error
    }
  }, []);

  useEffect(() => {
    window.testChatNotification = () => {
      playNotificationSound();
    };

    return () => {
      delete window.testChatNotification;
    };
  }, [playNotificationSound]);

  // Web push (.kiro/specs/1598-chat-push-notifications-web, EARS 2/3) — asks
  // once per browser (push.js's own localStorage guard), registers the FCM
  // token, no-ops entirely if the VAPID key isn't configured yet or the
  // browser doesn't support push. Background pushes (tab not focused) are
  // handled by the service worker directly — nothing to wire here for that.
  useEffect(() => {
    if (!userId || !firebaseAuthReady) return;
    requestPushPermission(userId).catch(() => null);
  }, [userId, firebaseAuthReady]);

  // EARS 5: a push that arrives while the tab IS focused feeds the SAME
  // toast as the Firestore-listener path above, instead of a second,
  // parallel notification UI — and applies the identical window.activeChatId
  // dedup #1596 established, using the conversationId the Cloud Function
  // attaches as `data` (sendMessagePush.js).
  useEffect(() => {
    const unsubscribe = listenForForegroundPush(payload => {
      const conversationId = payload.data?.conversationId;
      if (conversationId && window.activeChatId === conversationId) return;
      // Another centre's message (pushes carry their providerId): not here.
      const pushProvider = payload.data?.providerId;
      if (pushProvider && String(pushProvider) !== workingProviderId()) return;
      // Away from chat screens the Firestore listener opens the floating window.
      if (!isOnChatScreen()) return;
      if (toastedRecently(conversationId)) return;

      playNotificationSound();
      showToast({
        title: payload.notification?.title || t('CHAT.newMessage'),
        message: payload.notification?.body || t('CHAT.youReceivedMessage'),
        type: payload.data?.type || 'chat',
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [showToast, playNotificationSound, t]);

  useEffect(() => {
    if (!userId || !firebaseAuthReady) {
      setHasUnreadMessages(false);
      return;
    }

    const updateTotalCount = () => {
      const total = chatCountRef.current + channelCountRef.current;

      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        previousTotalRef.current = total;
        previousChatCountRef.current = chatCountRef.current;
        previousChannelCountRef.current = channelCountRef.current;
      } else {
        previousTotalRef.current = total;
        previousChatCountRef.current = chatCountRef.current;
        previousChannelCountRef.current = channelCountRef.current;
      }

      setHasUnreadMessages(total > 0);
    };

    // Both chats and channels are now the same shape — a list of conversation
    // docs with `lastMessage`/`readReceipts` — so one function handles the
    // "did a NEW unread arrive" detection for either
    // (.kiro/specs/1596-chat-notifications-rework). Previously chats were a
    // discrete events array (notifications/{userId}.unreadChats) and
    // channels were pre-filtered docs — two different shapes needing two
    // different comparisons; not anymore.
    const notifyIfNew = (all, type) => {
      // Only the provider being worked on (the one in the URL, else the one
      // loaded): another centre's messages don't toast or count here.
      const current = workingProviderId();
      const conversations = all.filter(c => current && String(c.providerId) === current);
      const unread = conversations.filter(c => isConversationUnread(c.lastMessage, c.readReceipts, userId));
      const countRef = type === 'channel' ? channelCountRef : chatCountRef;
      countRef.current = unread.length;

      // Fires on every NEW message in any still-unread conversation, not just
      // the first time a conversation BECOMES unread. Gating on
      // `unread.length > previousCount` (the count of unread conversations
      // going up) meant a second message in the same already-unread
      // conversation never re-triggered — its own conversation-doc lastMessage
      // changed, but the conversation COUNT didn't. `notificationId` already
      // encodes the latest message's own createdAt, so comparing it directly
      // is both necessary and sufficient — the count check was actively
      // wrong, not just redundant (found via manual QA: "só notifica a
      // primeira mensagem, a segunda não").
      // The first look at a provider (opening it, or switching to it) takes what's
      // already there as seen: only messages arriving from then on toast.
      const baselineKey = `${current}_${type}`;
      const firstLook = !(baselineKey in baselineRef.current);
      if (firstLook) {
        const newest = [...unread].sort((a, b) => (b.lastMessage?.createdAt || 0) - (a.lastMessage?.createdAt || 0))[0];
        baselineRef.current[baselineKey] = newest ? `${newest.id}_${type}_${newest.lastMessage?.createdAt || 0}` : null;
      }
      if (!firstLook && unread.length > 0 && isInitializedRef.current) {
        const sorted = [...unread].sort((a, b) => (b.lastMessage?.createdAt || 0) - (a.lastMessage?.createdAt || 0));
        const latest = sorted[0];
        const info = latest.lastMessage || {};
        const notificationId = `${latest.id}_${type}_${info.createdAt || 0}`;
        const isActiveConversation = window.activeChatId && latest.id === window.activeChatId;

        const alreadyThere = baselineRef.current[baselineKey] === notificationId;
        if (!alreadyThere && notificationId !== lastNotificationRef.current && !isActiveConversation) {
          lastNotificationRef.current = notificationId;
          if (toastedRecently(latest.id)) return updateTotalCount();

          // Away from every chat screen: open the conversation in the floating
          // window (unless it's busy with another one), so the admin can answer
          // without leaving what they're doing.
          const dock = chatDock.get();
          if (!isOnChatScreen() && (!dock.conversation || dock.minimized)) {
            playNotificationSound();
            chatDock.open(dockConversationOf(latest, type, userId));
            return updateTotalCount();
          }

          playNotificationSound();

          // Deep-link the toast to the SPECIFIC conversation it's about,
          // not just the chat page in general — read by chat/index.js's own
          // openChat/openChannel query-param handling.
          const href =
            type === 'channel'
              ? `/providers/${latest.providerId}/chat?openChannel=${latest.id}&tab=channel`
              : `/providers/${latest.providerId}/chat?openChat=${latest.members?.find(m => String(m) !== String(userId))}`;

          showToast({
            title: type === 'channel' ? latest.name || t('CHAT.newMessageInChannel') : info.senderName || t('CHAT.newMessage'),
            // The text, or "📷 Foto" for a photo (file, voice note) — never its file link.
            message: previewOf(info, userId, t) || (type === 'channel' ? t('CHAT.someonePostedInChannel') : t('CHAT.youReceivedMessage')),
            type,
            senderImage: info.senderImage,
            providerName: info.providerName,
            href,
          });
        }
      }

      updateTotalCount();
    };

    // Channels have no per-user membership list of their own (every
    // customer/resource of a provider is an implicit member — see
    // SidebarChannel.js) — the closest cheap source for "every provider this
    // user should watch channels for" is the set of providerIds already
    // present in their own chats. KNOWN LIMITATION (design.md §4): a
    // provider the user has never 1:1-chatted with won't surface a channel
    // toast until they do. No global "my providers" list exists to read
    // instead today.
    let unsubscribeChannels = () => {};
    const unsubscribeChats = listenToMyChats(userId, chats => {
      notifyIfNew(chats, 'chat');

      const providerIds = [...new Set(chats.map(chat => chat.providerId).filter(Boolean))];
      unsubscribeChannels();
      unsubscribeChannels = listenToAllChannelsForUser(providerIds, channels => notifyIfNew(channels, 'channel'));
    });

    return () => {
      if (typeof unsubscribeChats === 'function') {
        unsubscribeChats();
      }
      if (typeof unsubscribeChannels === 'function') {
        unsubscribeChannels();
      }
    };
  }, [userId, firebaseAuthReady, showToast, playNotificationSound, t]);

  return { hasUnreadMessages };
};

export default useUnreadMessages;
